import type {DiffAnchor, LineRange} from '@github-ui/diffs/types'
import DiffRowSelectedChecker from '../../../helpers/diff-row-selected-checker'
import {groupDiffLines, isEmptyDiffLine} from '../../../helpers/line-helpers'
import type {ClientDiffLine, DiffLine, DiffLineKey, DiffLineWithSelectionInfo, DiffOrientation} from '../../../types'
import {matchHash} from '@github-ui/diffs/document-hash-helpers'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

export function diffLineKey(diffLine: DiffLineWithSelectionInfo | DiffLine): DiffLineKey {
  return `b:${diffLine.blobLineNumber}-l:${diffLine.left}-r:${diffLine.right}`
}

export function isBeforePreviouslySelectedItem({
  current,
  currentOrientation,
  previous,
  previousOrientation,
}: {
  current: DiffLineWithSelectionInfo
  currentOrientation: DiffOrientation
  previous: DiffLineWithSelectionInfo | undefined
  previousOrientation: DiffOrientation
}): boolean {
  if (!previous) return false

  if (
    previousOrientation === 'left' &&
    currentOrientation === 'left' &&
    current.left &&
    previous.left &&
    previous.left > current.left
  ) {
    return true
  }

  if (
    previousOrientation === 'right' &&
    currentOrientation === 'right' &&
    current.right &&
    previous.right &&
    previous.right > current.right
  ) {
    return true
  }

  if (
    previousOrientation === 'right' &&
    currentOrientation === 'left' &&
    current.right &&
    previous.right &&
    previous.right > current.right
  ) {
    return true
  }

  if (
    previousOrientation === 'left' &&
    currentOrientation === 'right' &&
    current.left &&
    previous.left &&
    previous.left > current.left
  ) {
    return true
  }

  return false
}

export function getSelectedLineKeys({
  diffAnchor,
  diffLines,
  isSplitView,
  lineRange,
}: {
  diffLines: DiffLineWithSelectionInfo[]
  diffAnchor: DiffAnchor
  isSplitView: boolean
  lineRange: LineRange | undefined
}): {
  selectedLineKeys: Set<DiffLineKey>
  selectedLineRange?: LineRange | undefined
  startOrientation?: DiffOrientation | undefined
} {
  const selectedLineKeys = new Set<DiffLineKey>([])

  if (diffLines.length === 0) {
    return {selectedLineKeys}
  }

  if (!lineRange) {
    return {selectedLineKeys: new Set<DiffLineKey>([])}
  }

  const {startLineNumber, startOrientation, endLineNumber, endOrientation} = lineRange

  let leftLines: ClientDiffLine[] | DiffLineWithSelectionInfo[] = diffLines
  // Unified diff view doesn't have a concept of rightLines, but are grouping and line selection checker will use rightLines for determining selected state if rightLines is defined
  // Setting this to undefined will ensure we only calculate selection or leftlines for Unified diff view.
  let rightLines: ClientDiffLine[] | undefined = undefined

  if (isSplitView) {
    const grouped = groupDiffLines(diffLines)
    leftLines = grouped.leftLines
    rightLines = grouped.rightLines
  }

  const selectedLineRange: LineRange = {
    diffAnchor,
    endLineNumber: endLineNumber ?? startLineNumber,
    endOrientation: endOrientation ?? startOrientation,
    startLineNumber,
    startOrientation,
  }

  const checker = DiffRowSelectedChecker({
    selectedLineRange,
    leftLines,
    rightLines,
  })

  for (let i = 0; i < checker.selectedLeftLines.length; i++) {
    const diffLine = checker.selectedLeftLines[i]
    if (diffLine && !isEmptyDiffLine(diffLine) && diffLine.key) {
      selectedLineKeys.add(diffLine.key)
    }
  }

  for (let i = 0; i < checker.selectedRightLines.length; i++) {
    const diffLine = checker.selectedRightLines[i]
    if (diffLine && !isEmptyDiffLine(diffLine) && diffLine.key) {
      selectedLineKeys.add(diffLine.key)
    }
  }

  return {selectedLineKeys, startOrientation, selectedLineRange}
}

export function getLastValue<T>(set: Set<T>) {
  let value: T | undefined
  // ; is needed here to ensure that the last iterator is run, otherwise this will return the first value of the Set<T>
  for (value of set);
  return value
}

export function getFirstValue<T>(set: Set<T>) {
  return set.values().next().value
}

export function getSelectedLineRangeFromUrl(diffAnchor: DiffAnchor): LineRange | undefined {
  const match = matchHash(ssrSafeLocation.hash)
  if (!match) return

  // match[0] is the full match so start at 1
  const diffAnchorInHash = match[1]

  if (diffAnchorInHash !== diffAnchor) return

  // start of range
  const startOrientation = match[2] ? (match[2] === 'L' ? 'left' : 'right') : undefined
  const startLineNumber = match[3] ? parseInt(match[3]) : undefined
  // end of range
  const endOrientation = match[4] ? (match[4] === 'L' ? 'left' : 'right') : undefined
  const endLineNumber = match[5] ? parseInt(match[5]) : undefined

  if (!startOrientation || !startLineNumber) return

  return {
    diffAnchor,
    endOrientation: endOrientation ?? startOrientation,
    endLineNumber: endLineNumber ?? startLineNumber,
    startOrientation,
    startLineNumber,
  }
}
